import log from "logbeacon"; // 假设你有一个日志工具
import {
  getSizes,
  getSVG,
  handleChartResize,
  createOrSelectGroup,
  getScale,
  getMaxTextWidth,
} from "./utils"; // 从你的工具文件中导入
import type { GaugeOptions } from "./type"; // 导入我们定义的类型

/**
 * 绘制一个仪表盘图表
 * @param options - 图表配置项，遵循 GaugeOptions 接口
 * @returns 返回一个包含 cleanup 方法的对象，用于销毁图表实例和事件监听
 */
export const drawGauge = (options: GaugeOptions): { cleanup: () => void } => {
  const { el, value, segments, style } = options;
  const d3 = (window as any)?.d3;

  const containerEl =
    typeof el === "string" ? document.querySelector<HTMLElement>(el) : el;

  if (!containerEl) {
    log.error("[文案]GaugeChart: The container element is not found.");
    return { cleanup: () => {} };
  }
  if (!segments || segments.length === 0) {
    log.error("[文案]GaugeChart: The `segments` data is required.");
    return { cleanup: () => {} };
  }
  if (!d3?.arc || !d3?.select) {
    log.warn("[drawGauge] d3 is unavailable, skip drawing");
    return { cleanup: () => {} };
  }

  // --- 默认样式配置 ---
  const mergedStyle = {
    arcBackgroundColor: "rgba(255, 255, 255, 0.05)",
    inactiveLabelColor: "var(--edgen-content_quaternary)",
    ...style,
  };

  const draw = () => {
    // --- 1. 初始化和尺寸计算 ---
    const { containerWidth, containerHeight } = getSizes(containerEl);
    const svg = getSVG(containerEl, "gauge-chart");
    if (!svg) {
      return;
    }
    svg.html(""); // 清空旧内容以便重绘
    const leftMaxTextWidth = getMaxTextWidth(
      segments[0].name.split(" "),
      svg,
      "12px",
    );
    const rightMaxTextWidth = getMaxTextWidth(
      segments[segments.length - 1].name.split(" "),
      svg,
      "12px",
    );
    const margin = {
      top: 24,
      right: rightMaxTextWidth,
      bottom: 15,
      left: leftMaxTextWidth,
    };
    // 半径计算时优先考虑高度，确保半圆能完整显示
    const radius = Math.min(
      (containerWidth - margin.left - margin.right) / 2,
      containerHeight - margin.top - margin.bottom,
    );
    const arcWidth = radius * 0.15; // 圆环的宽度
    const innerRadius = radius - arcWidth;
    const outerRadius = radius;

    // 创建主绘图区域并将其居中
    const chartGroup = createOrSelectGroup(svg, "chart-group").attr(
      "transform",
      `translate(${containerWidth / 2}, ${containerHeight - margin.bottom})`,
    );

    // --- 2. 数据处理和比例尺 ---
    const activeIndex = segments.findIndex((s) => s.name === value);
    if (activeIndex === -1) {
      log.warn(
        `[文案]GaugeChart: Active value "${value}" not found in segments.`,
      );
      return;
    }
    // const activeSegment = segments[activeIndex];

    // 将 segment 索引映射到角度的比例尺
    // D3 的角度：-PI/2 是顶部, 0 是右侧, PI/2 是底部
    const angleScale = getScale(
      [0, segments.length - 1],
      [-Math.PI / 2, Math.PI / 2],
    );

    // --- 3. 绘制背景圆环 ---

    // 【修正点 1】定义 backgroundArc 生成器
    const backgroundArc = d3
      .arc()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2)
      .cornerRadius(arcWidth / 2); // 添加圆角使两端变圆

    chartGroup
      .append("path")
      .attr("class", "background-arc")
      .attr("d", backgroundArc as any)
      .attr("fill", mergedStyle.arcBackgroundColor);

    // --- 4. 定义并绘制前景渐变圆环 ---
    if (activeIndex >= 0) {
      // 【修正点 2】定义 activeArc 生成器
      const activeArc = d3
        .arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .startAngle(-Math.PI / 2)
        .endAngle(() => {
          // 当 activeIndex 为 0 时，startAngle 和 endAngle 会相同，导致圆弧不可见。
          // 我们需要确保 endAngle 至少代表第一个分段的中间位置，使其可见。
          if (activeIndex === 0) {
            const segmentAngle = Math.PI / (segments.length - 1); // 单个分段所占的角度
            return angleScale(activeIndex) + segmentAngle / 2; // 结束于当前激活分段的中间
          }
          return angleScale(activeIndex);
        })
        .cornerRadius(arcWidth / 2); // 同样添加圆角

      // 定义渐变
      const defs = createOrSelectGroup(svg, "defs");
      const gradientId = `gauge-gradient-${Date.now()}`;
      const gradient = defs.append("linearGradient").attr("id", gradientId);

      const gradientSegments = segments.slice(0, activeIndex + 1);

      if (gradientSegments.length === 1) {
        gradient
          .append("stop")
          .attr("offset", "0%")
          .attr("stop-color", gradientSegments[0].color);
        gradient
          .append("stop")
          .attr("offset", "100%")
          .attr("stop-color", gradientSegments[0].color);
      } else {
        gradientSegments.forEach((segment, i) => {
          gradient
            .append("stop")
            .attr("offset", `${(i / (gradientSegments.length - 1)) * 100}%`)
            .attr("stop-color", segment.color);
        });
      }

      // 绘制前景
      chartGroup
        .append("path")
        .attr("class", "active-arc")
        .attr("d", activeArc as any) // 此处现在可以正确引用 activeArc
        .attr("fill", `url(#${gradientId})`);
    }

    // --- 5. 绘制标签 ---
    const labelRadius = outerRadius + 15;
    const labelsGroup = createOrSelectGroup(chartGroup, "labels-group");
    labelsGroup
      .selectAll(".arc-label")
      .data(segments)
      .join("text")
      .attr("class", "arc-label")
      .attr("transform", (_d, i) => {
        const angle = angleScale(i);
        let x = labelRadius * Math.cos(angle - Math.PI / 2);
        let y = labelRadius * Math.sin(angle - Math.PI / 2);
        if (i === 0 || i === segments.length - 1) {
          y -= 15;
        }
        if (i === 0) {
          x += 10;
        }
        if (i === segments.length - 1) {
          x -= 10;
        }
        return `translate(${x}, ${y})`;
      })
      .attr("text-anchor", (_d, i) => {
        if (i === 0) {
          return "end";
        }
        if (i === segments.length - 1) {
          return "start";
        }
        return "middle";
      })
      .attr("dominant-baseline", (_d, i) => {
        if (i === 0) {
          return "text-after-edge";
        }
        if (i === segments.length - 1) {
          return "text-after-edge";
        }
        return "middle";
      })
      .attr("fill", (d, i) =>
        i === activeIndex ? d.activeLabelColor : mergedStyle.inactiveLabelColor,
      )
      .style("font-size", "12px")
      .each(function (d) {
        const words = d.name.split(" ");
        const textNode = d3.select(this);
        textNode.text(null); // 清空现有文本

        words.forEach((word, i) => {
          textNode
            .append("tspan")
            .attr("x", 0)
            .attr("dy", i === 0 ? "0.32em" : "1.2em") // 第一行使用初始偏移，后续行增加行高
            .text(word);
        });
      });
  };
  // 初始绘制
  draw();

  // 设置响应式
  const { cleanup } = handleChartResize({
    el: containerEl,
    drawChart: draw,
  });

  return {
    cleanup,
  };
};
